import {Tooltip, IconButton, openDialog} from 'platform/components';
import {VStack, Show, HStack, Heading, Text} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {SmsGetKonzultaCredentialsApiResponse} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {SendCustomerEmailForm} from '../../SendCustomerEmailForm/SendCustomerEmailForm';
import {SendCustomerSmsForm} from '../../SendCustomerSmsForm/SendCustomerSmsForm';

interface ParameterBlockProps extends TestIdProps {
  primaryParameter?: string;
  secondaryParameter?: string;
  tertiaryParameter?: string;
  resourceId?: EntityResourceIds;
  recordId?: string;
  konzultaConfig?: SmsGetKonzultaCredentialsApiResponse;
  isEmailConfigEnabled?: boolean;
}

export function ParameterBlock(props: ParameterBlockProps) {
  return (
    <VStack spacing={1} align="flex-end">
      <Show when={props.primaryParameter}>
        <HStack spacing={1} align="center">
          <Heading
            size={3}
            isSingleLine
            data-testid={suffixTestId('entityHeader-primaryParameter', props)}
          >
            {props.primaryParameter}
          </Heading>
          <Show
            when={
              props.resourceId === 'CUSTOMER' &&
              props.primaryParameter !== i18n.t('entity.customer.labels.noPhoneNumber')
            }
          >
            <Tooltip
              label={
                props.konzultaConfig?.enabled
                  ? i18n.t('general.actions.sendMessage')
                  : i18n.t('general.notifications.setupSmsProvider')
              }
              placement="top-end"
            >
              <IconButton
                severity="informational"
                data-testid="open-send-message-modal"
                icon="communication/textsms"
                isDisabled={!props.konzultaConfig?.enabled}
                onClick={() =>
                  openDialog(
                    <SendCustomerSmsForm
                      customerId={props.recordId}
                      recordId={props.recordId}
                      resourceId={props.resourceId}
                    />,
                    {
                      scrollBehavior: 'outside',
                      size: 'large',
                      title: i18n.t('general.labels.smsMessage'),
                    }
                  )
                }
              />
            </Tooltip>
          </Show>
          <Show when={isNotNilOrEmpty(props.tertiaryParameter)}>
            <Heading
              size={3}
              isSingleLine
              data-testid={suffixTestId('entityHeader-tertiaryParameter', props)}
            >
              {`/ ${props.tertiaryParameter ?? ''}`}
            </Heading>
          </Show>
        </HStack>
      </Show>
      <Show when={props.secondaryParameter}>
        <HStack spacing={1} align="center">
          <Text
            size="base"
            color="secondary"
            noWrap
            data-testid={suffixTestId('entityHeader-secondaryParameter', props)}
          >
            {props.secondaryParameter}
          </Text>
          <Show
            when={
              props.resourceId === 'CUSTOMER' &&
              props.secondaryParameter !== i18n.t('entity.customer.labels.noEmail')
            }
          >
            <Tooltip
              label={
                props.isEmailConfigEnabled
                  ? i18n.t('general.actions.sendEmail')
                  : i18n.t('general.notifications.setupEmailProvider')
              }
              placement="top-end"
            >
              <IconButton
                severity="informational"
                data-testid="open-send-email-modal"
                icon="communication/email"
                isDisabled={!props.isEmailConfigEnabled}
                onClick={() =>
                  openDialog(
                    <SendCustomerEmailForm
                      customerId={props.recordId}
                      recordId={props.recordId}
                      resourceId={props.resourceId}
                    />,
                    {
                      scrollBehavior: 'outside',
                      size: 'large',
                      title: i18n.t('general.actions.sendEmail'),
                    }
                  )
                }
              />
            </Tooltip>
          </Show>
        </HStack>
      </Show>
    </VStack>
  );
}
